import { supabase } from "@/integrations/supabase/client";
import { fixPolishGrammar } from "@/lib/seo-routes";
import { buildMeta } from "@/lib/seo";

export interface SeoPageData {
  slug: string;
  page_type: "city" | "route" | "country" | "theme";
  from_iata: string | null;
  to_iata: string | null;
  title: string;
  meta_description: string;
  h1: string;
  intro: string;
  sections: { best_time: string; prices: string; tips: string; description: string };
  faq: { q: string; a: string }[];
  section_titles?: { description: string; best_time: string; tips: string };
  editorial_sections?: { title: string; body: string }[];
  sources?: { label: string; url: string }[];
}

interface LoadParams {
  type: "city" | "route" | "country" | "theme";
  from?: string;
  to?: string;
  fromName?: string;
  toName?: string;
  slug?: string;
  label?: string;
  description?: string;
}

function computeSlug(p: LoadParams): string {
  switch (p.type) {
    case "city": return `loty-${p.from?.toLowerCase() ?? ""}`;
    case "route": return `loty-${p.from?.toLowerCase() ?? ""}-${p.to?.toLowerCase() ?? ""}`;
    case "country": return `kraj-${p.slug}`;
    case "theme": return `temat-${p.slug}`;
  }
}

// Cache-only read — fast, deterministic, safe in SSR.
// Returns null when the page hasn't been pre-generated yet.
export async function loadSeoPage(p: LoadParams): Promise<SeoPageData | null> {
  const slug = computeSlug(p);
  try {
    const { data } = await supabase
      .from("seo_pages")
      .select("*")
      .eq("slug", slug)
      .maybeSingle();
    return (data as unknown as SeoPageData) ?? null;
  } catch {
    return null;
  }
}

// Fire-and-forget background generation (client-only).
// Visiting an ungenerated page triggers generation for the next visitor.
export function triggerSeoGeneration(p: LoadParams): void {
  if (typeof window === "undefined") return;
  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-seo-page`;
  fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
    },
    body: JSON.stringify(p),
    keepalive: true,
  }).catch(() => {});
}

export function buildSeoPageHead(
  page: SeoPageData | null,
  canonicalPath: string,
  fallbackTitle: string,
  fallbackDesc: string,
  alternates?: { hrefLang: string; href: string }[],
) {
  const title = fixPolishGrammar(page?.title ?? fallbackTitle);
  const description = fixPolishGrammar(page?.meta_description ?? fallbackDesc);
  const ogImageByType: Record<string, string> = {
    city: "https://tanielatanie.pl/og-city.jpg",
    route: "https://tanielatanie.pl/og-route.jpg",
    country: "https://tanielatanie.pl/og-country.jpg",
    theme: "https://tanielatanie.pl/og-theme.jpg",
  };
  const image = page ? ogImageByType[page.page_type] : undefined;
  // Strony tras (route) renderują bogate, deterministyczne sekcje
  // (dystans, miesięczny chart, top linie, FAQ + JSON-LD Trip) nawet bez
  // AI-treści — są indeksowalne od razu. Dla city/country/theme bez
  // wygenerowanej treści wciąż noindex, bo mają tylko boilerplate.
  const isRoutePath = /^\/loty\/[a-z]{3}\/[a-z]{3}$/.test(canonicalPath);
  const noindex = !page && !isRoutePath;
  const base = buildMeta({ title, description, path: canonicalPath, noindex, image, alternates });

  const breadcrumbName = fixPolishGrammar(page?.h1 ?? title);
  const scripts: Array<{ type: string; children: string }> = [
    {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Strona główna", item: `https://tanielatanie.pl/` },
          { "@type": "ListItem", position: 2, name: breadcrumbName, item: `https://tanielatanie.pl${canonicalPath}` },
        ],
      }),
    },
  ];
  if (page?.faq && page.faq.length > 0) {
    scripts.push({
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: page.faq.map((f) => ({
          "@type": "Question",
          name: fixPolishGrammar(f.q),
          acceptedAnswer: { "@type": "Answer", text: fixPolishGrammar(f.a) },
        })),
      }),
    });
  }

  return { ...base, scripts };
}
